/**
 * Phase 45 Plan 45-05 — Portfolio table.
 * Phase 50 Plan 50-08 — Extended with 'tier' group-by branch (MO-MULTI-01).
 *
 * Sticky 7-column header + row stream per UI-SPEC §Surface 1 Column grid.
 * When `groupBy !== 'none'` rows are split by customer, owner, or tier and
 * each group emits a PortfolioGroupRow rollup with aggregated metrics.
 *
 * Tier grouping (MO-MULTI-01 — single-group membership):
 *   Each project uses MIN(orderIndex) across its open SLA tasks as its tier
 *   bucket. Projects with no open SLA tasks group under "No tier" at bottom.
 *
 * Outer wrap: bg-ms-surface border rounded-lg overflow-hidden — matches
 * the table region UI-SPEC §Layout (px-7 pb-5 flex-1 overflow-auto in
 * caller, this card is the inner surface).
 */
import { Fragment } from 'react';
import { PortfolioRow } from './PortfolioRow';
import { PortfolioGroupRow } from './PortfolioGroupRow';
import { weightedMargin } from './money';
import { portfolioGridClass } from './portfolioGrid';
import { MoneyMetricLabel } from '../common/MoneyMetricLabel';
import type { GroupBy } from './GroupByToggle';
import type { PortfolioRow as PortfolioRowData } from '../../services/api/portfolio';

export interface PortfolioTableProps {
  rows: PortfolioRowData[];
  groupBy: GroupBy;
}

interface RowGroup {
  key: string;
  label: string;
  rows: PortfolioRowData[];
  /** orderIndex of the tier for this group (only set when groupBy === 'tier'). */
  tierOrderIndex?: number | null;
}

/**
 * Exported for unit testing (Plan 50-08).
 *
 * Returns a stable { key, label } for a row given the current groupBy value.
 * - 'tier': key = 'tier:{orderIndex}' or 'tier:none'; label = 'P{n} — {name}' or 'No tier'
 */
export function groupKey(row: PortfolioRowData, groupBy: GroupBy): { key: string; label: string } {
  if (groupBy === 'customer') {
    return {
      key: row.customerId !== null ? `c-${row.customerId}` : 'c-none',
      label: row.customerName ?? 'No customer',
    };
  }
  if (groupBy === 'owner') {
    return {
      key: row.projectManager ? `o-${row.projectManager.id}` : 'o-none',
      label: row.projectManager
        ? `${row.projectManager.firstName} ${row.projectManager.lastName}`.trim()
        : 'No owner',
    };
  }
  if (groupBy === 'tier') {
    if (row.minOpenTierOrderIndex == null || row.minOpenTierName == null) {
      return { key: 'tier:none', label: 'No tier' };
    }
    return {
      key: `tier:${row.minOpenTierOrderIndex}`,
      label: `P${row.minOpenTierOrderIndex} — ${row.minOpenTierName}`,
    };
  }
  return { key: 'all', label: 'All projects' };
}

function buildGroups(rows: PortfolioRowData[], groupBy: GroupBy): RowGroup[] {
  if (groupBy === 'none') {
    return [{ key: 'all', label: 'All projects', rows }];
  }
  const buckets = new Map<string, RowGroup>();
  for (const row of rows) {
    const { key, label } = groupKey(row, groupBy);
    let bucket = buckets.get(key);
    if (!bucket) {
      const tierOrderIndex =
        groupBy === 'tier' ? (row.minOpenTierOrderIndex ?? null) : undefined;
      bucket = { key, label, rows: [], tierOrderIndex };
      buckets.set(key, bucket);
    }
    bucket.rows.push(row);
  }
  const groups = Array.from(buckets.values());
  if (groupBy === 'tier') {
    // For tier groups: P1 → P2 → ...; "No tier" always last.
    groups.sort((a, b) => {
      if (a.key === 'tier:none') return 1;
      if (b.key === 'tier:none') return -1;
      return a.key.localeCompare(b.key);
    });
    return groups;
  }
  return groups.sort((a, b) => a.label.localeCompare(b.label));
}

function aggregate(rows: PortfolioRowData[]) {
  const totalHoursLogged = rows.reduce((acc, r) => acc + r.hoursLogged, 0);
  const totalRevenue = rows.reduce((acc, r) => acc + r.revenue, 0);
  const meanBurnPct =
    rows.length === 0 ? 0 : rows.reduce((acc, r) => acc + r.burnPct, 0) / rows.length;
  const anyBillable = rows.some((r) => r.billable);
  // MKS-328 — the group's margin is revenue-weighted, never a mean of the
  // rows' percentages: one tiny project must not count as much as the one
  // that carries the group.
  const margin = weightedMargin(
    rows.map((r) => ({ revenue: r.revenue, cost: r.cost ?? 0 })),
  );
  return { totalHoursLogged, totalRevenue, meanBurnPct, anyBillable, margin };
}

export function PortfolioTable({ rows, groupBy }: PortfolioTableProps) {
  const groups = buildGroups(rows, groupBy);
  // MKS-332 — the API omits `cost` for anyone who is not admin or finance, so
  // the Margin column (and the group rollup behind it) has nothing to show.
  // Decided ONCE here and passed down, so the header, the group rows and the
  // data rows cannot disagree about how many columns the grid has.
  const showMargin = rows.length === 0 || rows.some((r) => r.cost !== undefined);

  return (
    <div
      className="bg-ms-surface border border-ms-border-subtle rounded-lg overflow-hidden"
      role="table"
      aria-label="Portfolio projects"
    >
      {/* Sticky header */}
      <div
        className={`${portfolioGridClass(showMargin)} items-center bg-ms-surface-2 border-b border-ms-border-subtle sticky top-0 z-10`}
        role="row"
      >
        <div
          role="columnheader"
          className="px-4 py-2 text-left text-[10px] uppercase tracking-wider font-semibold text-ms-text-muted"
        >
          Project
        </div>
        <div
          role="columnheader"
          className="px-2 py-2 text-left text-[10px] uppercase tracking-wider font-semibold text-ms-text-muted"
        >
          Status
        </div>
        <div
          role="columnheader"
          className="px-2 py-2 text-left text-[10px] uppercase tracking-wider font-semibold text-ms-text-muted"
        >
          Burn vs plan
        </div>
        <div
          role="columnheader"
          className="px-2 py-2 text-right text-[10px] uppercase tracking-wider font-semibold text-ms-text-muted"
        >
          <MoneyMetricLabel metric="earned" align="right" />
        </div>
        {showMargin && (
          <div
            role="columnheader"
            className="px-2 py-2 text-right text-[10px] uppercase tracking-wider font-semibold text-ms-text-muted"
          >
            <MoneyMetricLabel metric="margin" align="right" />
          </div>
        )}
        <div
          role="columnheader"
          className="px-2 py-2 text-left text-[10px] uppercase tracking-wider font-semibold text-ms-text-muted"
        >
          Team
        </div>
        <div
          role="columnheader"
          className="px-4 py-2 text-left text-[10px] uppercase tracking-wider font-semibold text-ms-text-muted"
        >
          Due
        </div>
      </div>

      {/* Body */}
      <div role="rowgroup">
        {groups.map((group) => {
          const agg = aggregate(group.rows);
          return (
            <Fragment key={group.key}>
              {groupBy !== 'none' && (
                <PortfolioGroupRow
                  label={group.label}
                  projectCount={group.rows.length}
                  totalHoursLogged={agg.totalHoursLogged}
                  totalRevenue={agg.totalRevenue}
                  meanBurnPct={agg.meanBurnPct}
                  billable={agg.anyBillable}
                  margin={agg.margin}
                  costVisible={showMargin}
                  tierOrderIndex={group.tierOrderIndex}
                />
              )}
              {group.rows.map((row) => (
                <PortfolioRow key={row.id} row={row} showMargin={showMargin} />
              ))}
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}
